import WatchedCampaignActions from './WatchedCampaignActions';
import SourcesLoading, { SourcesProgress } from './SourcesLoading';
import React, { useEffect, useState } from 'react';
import {
  acknowledgeGoogleAdsSetup,
  activateConnection,
  campaignErrorMessage,
  disconnectConnection,
  getConnection,
  importCampaigns,
  listGoogleAdsCampaigns,
  listGoogleAdsCustomers,
  listImportedCampaigns,
  prepareConnection,
  provisionGoogleAds,
  resendGoogleAdsInvite,
  stopWatchingCampaign,
} from '../api/adpilotApi';

// Same product rule as Meta: import as many campaigns as you like, MONITOR at
// most CAMPAIGN_LIMIT per channel. A connection is per provider, so the
// backend's per-connection cap is exactly the per-channel cap.
const CAMPAIGN_LIMIT = 20;

// Provisioning form choices: Google requires an ISO 4217 currency and an IANA
// time zone at account creation, and NEITHER can ever change afterwards — the
// form says that out loud. The lists cover the markets AdPilot sells into;
// anything else needs support (deliberately not hiding that).
const CURRENCIES = ['USD', 'EUR', 'GBP', 'CAD', 'AUD', 'NZD', 'SGD', 'INR', 'JPY', 'BRL', 'MXN', 'CHF'];
const TIME_ZONES = [
  'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
  'America/Anchorage', 'Pacific/Honolulu', 'America/Mexico_City', 'America/Sao_Paulo',
  'America/Toronto', 'America/Vancouver', 'Europe/London', 'Europe/Dublin',
  'Europe/Paris', 'Europe/Berlin', 'Europe/Madrid', 'Europe/Amsterdam',
  'Europe/Warsaw', 'Europe/Istanbul', 'Africa/Johannesburg', 'Africa/Lagos',
  'Asia/Dubai', 'Asia/Kolkata', 'Asia/Bangkok', 'Asia/Singapore',
  'Asia/Hong_Kong', 'Asia/Shanghai', 'Asia/Tokyo', 'Asia/Seoul',
  'Australia/Sydney', 'Australia/Perth', 'Pacific/Auckland',
];

function tokenIsUsable(token) {
  if (!token) return false;
  if (!token.expires_at) return true;
  return new Date(token.expires_at).getTime() > Date.now();
}

/**
 * Path B of the chooser: the owner has no Google Ads account, so we create one
 * under AdPilot's manager account. One form, one shot: business name + billing
 * email + currency + time zone, then Google emails them an access invitation
 * and billing stays in their own hands (their card, at ads.google.com).
 */
function GoogleAdsProvisionForm({ user, busy, onProvisioned, onBack }) {
  const [businessName, setBusinessName] = useState('');
  const [email, setEmail] = useState(user.email || '');
  const [currency, setCurrency] = useState('USD');
  const [timeZone, setTimeZone] = useState(() => {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return 'America/New_York'; }
  });
  const [formError, setFormError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!businessName.trim() || !email.trim() || !currency || !timeZone) {
      setFormError('All four fields are required — Google Ads rejects an account without them.');
      return;
    }
    setSubmitting(true);
    setFormError(null);
    try {
      const result = await provisionGoogleAds({
        userId: user.id, businessName: businessName.trim(), currency, timeZone, email: email.trim(),
      });
      onProvisioned(email.trim(), businessName.trim(), result.customer);
    } catch (error) {
      setFormError(campaignErrorMessage(error, 'Could not create the Google Ads account'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <ol style={{ margin: '0 0 14px', paddingLeft: 20, lineHeight: 1.7 }} className="card-copy">
        <li>We register the account under AdPilot’s manager — Google is notified in the same step.</li>
        <li>Google emails <strong>your address</strong> an access invitation. Accept it and sign in to Google Ads.</li>
        <li>Add your own payment method there — Google bills media to YOUR card directly; AdPilot only ever charges the subscription.</li>
        <li>Come back here and AdPilot is watching.</li>
      </ol>
      <label className="builder-field">
        <span>Business name</span>
        <input
          type="text"
          aria-label="Business name"
          value={businessName}
          maxLength={100}
          onChange={(event) => setBusinessName(event.target.value)}
        />
      </label>
      <label className="builder-field">
        <span>Email for the Google Ads access invitation</span>
        <input
          type="email"
          aria-label="Invitation email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />
        <small>Use a Google identity you can sign in to Google Ads with — that’s the account Google will invite.</small>
      </label>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <label className="builder-field" style={{ flex: '1 1 140px' }}>
          <span>Account currency</span>
          <select aria-label="Account currency" value={currency} onChange={(event) => setCurrency(event.target.value)}>
            {CURRENCIES.map((code) => <option key={code} value={code}>{code}</option>)}
          </select>
        </label>
        <label className="builder-field" style={{ flex: '2 1 220px' }}>
          <span>Account time zone</span>
          <select aria-label="Account time zone" value={timeZone} onChange={(event) => setTimeZone(event.target.value)}>
            {TIME_ZONES.map((zone) => <option key={zone} value={zone}>{zone.replace(/_/g, ' ')}</option>)}
          </select>
        </label>
      </div>
      <p className="card-copy" style={{ marginTop: 0 }}>Currency and time zone are permanent — Google Ads never lets them change once an account exists.</p>
      {formError && <div className="notice notice-error" role="alert">{formError}</div>}
      <div className="campaign-actions" style={{ marginTop: 10 }}>
        <button type="button" className="btn btn-primary" disabled={busy || submitting} onClick={submit}>
          {submitting ? 'Creating your account…' : 'Create my Google Ads account'}
        </button>
        <button type="button" className="btn btn-secondary" onClick={onBack}>Back</button>
      </div>
    </div>
  );
}

/**
 * The persistent post-provision checklist (setup_state === 'awaiting_billing').
 * It survives reloads because the flag lives on the connection row; the
 * acknowledge button clears it once billing is actually in place.
 */
function SetupChecklist({ customerId, businessName, invitationEmail, user, busy, onAcknowledged }) {
  const [inviteEmail, setInviteEmail] = useState(invitationEmail || user.email || '');
  const [checklistNote, setChecklistNote] = useState(null);
  const [working, setWorking] = useState(false);

  const resend = async () => {
    setWorking(true);
    setChecklistNote(null);
    try {
      await resendGoogleAdsInvite({ userId: user.id, email: inviteEmail.trim() });
      setChecklistNote({ kind: 'ok', text: `Invitation re-sent to ${inviteEmail.trim()}.` });
    } catch (error) {
      setChecklistNote({ kind: 'error', text: campaignErrorMessage(error, 'Could not resend the invitation') });
    } finally {
      setWorking(false);
    }
  };

  const acknowledge = async () => {
    setWorking(true);
    setChecklistNote(null);
    try {
      const { connection } = await acknowledgeGoogleAdsSetup({ userId: user.id });
      onAcknowledged(connection);
    } catch (error) {
      setChecklistNote({ kind: 'error', text: campaignErrorMessage(error, 'Could not save that — try again') });
      setWorking(false);
    }
  };

  return (
    <section className="card" aria-label="Google Ads setup checklist">
      <p className="eyebrow">Finish setup</p>
      <h2 className="card-title">Your Google Ads account is ready — {businessName}</h2>
      <p className="card-copy" style={{ fontWeight: 600 }}>Account id: {customerId}</p>
      <ol className="card-copy" style={{ margin: '12px 0', paddingLeft: 20, lineHeight: 1.8 }}>
        <li><strong>Check your inbox:</strong> Accept Google’s account access invitation to sign in. If it has not arrived, use Resend invitation below.</li>
        <li><strong>Add billing:</strong> in Google Ads, open <a href="https://ads.google.com/aw/billing" target="_blank" rel="noopener noreferrer">Tools &amp; settings → Billing</a> and attach your payment method. Google charges media to your card directly — AdPilot never touches media spend.</li>
        <li><strong>Come back here:</strong> once billing is active your ads can serve, and AdPilot can watch and optimize campaigns the moment they exist.</li>
      </ol>
      <label className="builder-field">
        <span>Invitation email</span>
        <input
          type="email"
          aria-label="Invitation email"
          value={inviteEmail}
          onChange={(event) => setInviteEmail(event.target.value)}
        />
      </label>
      {checklistNote && (
        <div className={checklistNote.kind === 'error' ? 'notice notice-error' : 'notice'} role="status">{checklistNote.text}</div>
      )}
      <div className="campaign-actions" style={{ marginTop: 12 }}>
        <button type="button" className="btn btn-secondary" disabled={busy || working || !inviteEmail.trim()} onClick={resend}>
          {working ? 'Working…' : 'Resend invitation'}
        </button>
        <button type="button" className="btn btn-primary" disabled={busy || working} onClick={acknowledge}>
          Billing is set — continue
        </button>
      </div>
    </section>
  );
}

/**
 * The Google Ads side of the Connect page.
 *
 * Connection lifecycle: choose a path (existing account → OAuth consent → pick
 * account; or no account → we provision one under our MCC) → billing checklist
 * for provisioned accounts → active (watch/import lists, same primitives as
 * Meta). Disconnect unwinds all of it.
 */
function GoogleAdsConnectCard({ user, oauthState }) {
  const [connection, setConnection] = useState(null);
  const [authorizationUrl, setAuthorizationUrl] = useState(null);
  const [customers, setCustomers] = useState([]);
  // Distinguish "the list came back empty" from "the list call failed" — the
  // card used to show the failure notice AND "No Google Ads accounts are
  // visible to this Google login", which asserts an emptiness nobody verified.
  const [customersFailed, setCustomersFailed] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(!user.preview);
  // Monitoring state: the imported catalog (watched ⊆ imported), the live
  // browse list, and the pending watch selection.
  const [imported, setImported] = useState([]);
  const [available, setAvailable] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showImporter, setShowImporter] = useState(false);
  // 'choose' | 'existing' | 'new' — the unconnected chooser shows both paths;
  // picking one narrows the card to it.
  const [path, setPath] = useState('choose');
  const [provisionedName, setProvisionedName] = useState(null);
  const [preparingConnection, setPreparingConnection] = useState(false);
  const userId = user.id;

  const refreshCampaigns = async (current) => {
    const [rows, browse] = await Promise.all([
      listImportedCampaigns({ userId, connectionId: current.connection_id, scope: 'all' }),
      listGoogleAdsCampaigns({ userId, connectionId: current.connection_id }),
    ]);
    setImported(Array.isArray(rows) ? rows : []);
    setAvailable(Array.isArray(browse) ? browse : []);
  };

  const reloadConnection = async () => {
    try {
      return (await getConnection({ userId, provider: 'google-ads' })).connection;
    } catch (reloadError) {
      if (reloadError?.response?.status !== 404) throw reloadError;
      return null;
    }
  };

  useEffect(() => {
    if (user.preview) return undefined;
    let cancelled = false;
    setLoading(true);
    const load = async () => {
      try {
        // Only surface an OAuth error here when it was OUR provider's flow —
        // a failed Meta consent must not paint the Google card red.
        if (oauthState?.oauth_error && oauthState?.oauth_provider === 'google-ads') {
          setError(oauthState.oauth_error === 'access_denied'
            ? 'Google sign-in or consent was not completed. Connect again, finish Google’s identity check, and approve Google Ads access.'
            : oauthState.oauth_error_description || oauthState.oauth_error);
          setPath('existing');
        }
        const current = await reloadConnection();
        if (cancelled) return;

        if (current?.access_role && current.access_role !== 'owner') {
          setConnection(current);
          if (current.ad_account_id && current.provider === 'google-ads' && current.setup_state !== 'awaiting_billing') {
            await refreshCampaigns(current);
          }
          return;
        }

        if (!current || !tokenIsUsable(current.token)) {
          if (current) setConnection(current);
          setPreparingConnection(true);
          try {
            const prepared = await prepareConnection({ userId, provider: 'google-ads' });
            if (cancelled) return;
            setAuthorizationUrl(prepared.authorization_url);
            setConnection(prepared.connection || current);
          } finally {
            if (!cancelled) setPreparingConnection(false);
          }
          return;
        }

        setConnection(current);
        if (!current.ad_account_id) {
          try {
            // Guard the shape: a mocked or misbehaving API must degrade to the
            // empty-list copy, not crash the card.
            const listed = await listGoogleAdsCustomers({ userId, connectionId: current.connection_id });
            setCustomers(Array.isArray(listed) ? listed : []);
            setCustomersFailed(false);
          } catch (listError) {
            setCustomersFailed(true);
            setError(campaignErrorMessage(listError, 'Failed to list your Google Ads accounts'));
          }
        } else if (current.provider === 'google-ads' && current.setup_state !== 'awaiting_billing') {
          // Guard on the row's own provider: the campaign catalog must never
          // be loaded off a connection that is not actually Google Ads (a
          // misrouted server response would otherwise burn the shared
          // campaign-links reads against the wrong channel). And while billing
          // setup is pending, the checklist is the job — campaigns come after.
          await refreshCampaigns(current);
        }
      } catch (loadError) {
        if (!cancelled) setError(campaignErrorMessage(loadError, 'Failed to load your Google Ads connection'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, user.preview, oauthState?.oauth_success, oauthState?.oauth_error]);

  const retryAuthorization = async () => {
    setPreparingConnection(true);
    setError(null);
    try {
      const prepared = await prepareConnection({ userId, provider: 'google-ads' });
      setAuthorizationUrl(prepared.authorization_url);
      setConnection(prepared.connection || connection);
    } catch (prepareError) {
      setError(campaignErrorMessage(prepareError, 'Failed to prepare Google Ads connection'));
    } finally {
      setPreparingConnection(false);
    }
  };

  const selectCustomer = async (customerId) => {
    if (busy) return;
    setBusy(true);
    setLoading(true);
    setError(null);
    try {
      const result = await activateConnection({
        userId, connectionId: connection.connection_id, adAccountId: customerId,
      });
      const next = { ...connection, ...result.connection };
      setConnection(next);
      setMessage('Google Ads account connected. Choose the campaigns AdPilot should watch.');
      await refreshCampaigns(next);
    } catch (selectError) {
      setError(campaignErrorMessage(selectError, 'Failed to select the Google Ads account'));
    } finally {
      setBusy(false);
      setLoading(false);
    }
  };

  const handleProvisioned = async (provisionEmail, businessName, customer) => {
    setError(null);
    try {
      const fresh = await reloadConnection() || { setup_state: 'awaiting_billing' };
      setConnection({
        ...fresh,
        setup_state: fresh.setup_state || 'awaiting_billing',
        setup_email: fresh.setup_email || provisionEmail,
      });
      setProvisionedName(businessName || null);
      setMessage(customer?.invitation_sent === false
        ? 'Account created. Finish setup below.'
        : `Account created. Google sent the access invitation to ${provisionEmail} — finish setup below.`);
      if (customer?.invitation_sent === false) {
        setError(`The account was created, but the invitation was not sent: ${customer.invitation_error}. Use Resend invitation below.`);
      }
    } catch (provisionLoadError) {
      setError(campaignErrorMessage(provisionLoadError, 'The account was created — reload the page to continue setup'));
    }
  };

  const handleAcknowledge = async (refreshed) => {
    setLoading(true);
    setConnection(refreshed);
    setMessage('Setup confirmed. Connect campaigns to AdPilot below.');
    try { await refreshCampaigns(refreshed); }
    catch (loadError) { setError(campaignErrorMessage(loadError, 'Could not load Google Ads campaigns')); }
    finally { setLoading(false); }
  };

  const handleDisconnect = async () => {
    if (busy) return;
    if (!window.confirm(
      'Disconnect Google Ads from AdPilot?\n\n'
      + 'Syncing and analysis stop right away, and AdPilot stops reading this account. '
      + 'Imported campaigns, briefs, and history are kept — reconnect any time.',
    )) return;
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      await disconnectConnection({ userId, provider: 'google-ads' });
      setConnection(null);
      setCustomers([]);
      setImported([]);
      setAvailable([]);
      setSelectedIds(new Set());
      setShowImporter(false);
      setProvisionedName(null);
      setPath('choose');
      setMessage('Google Ads disconnected. Your history is kept — reconnect any time.');
    } catch (disconnectError) {
      setError(campaignErrorMessage(disconnectError, 'Could not disconnect Google Ads'));
    } finally {
      setBusy(false);
    }
  };

  const watchedList = imported.filter((campaign) => campaign.watched !== false);
  const slots = Math.max(0, CAMPAIGN_LIMIT - watchedList.length);
  const watchedIds = new Set(watchedList.map((campaign) => String(campaign.platform_campaign_id)));
  const unwatchedImported = imported.filter((campaign) => campaign.watched === false);
  const unimportedCount = available
    .filter((campaign) => !imported.some((item) => String(item.platform_campaign_id) === String(campaign.platform_campaign_id)))
    .length;

  const toggleCampaign = (platformCampaignId) => {
    if (watchedIds.has(String(platformCampaignId))) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(platformCampaignId)) next.delete(platformCampaignId);
      else if (next.size < slots) next.add(platformCampaignId);
      return next;
    });
  };

  const handleWatchSelected = async (campaignIds = [...selectedIds]) => {
    if (busy) return;
    if (campaignIds.length === 0) {
      setError('Choose at least one campaign.');
      return;
    }
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      await importCampaigns({ userId, connectionId: connection.connection_id, campaignIds });
      setSelectedIds(new Set());
      setShowImporter(false);
      await refreshCampaigns(connection);
      setMessage('AdPilot is watching those campaigns now.');
    } catch (importError) {
      setError(campaignErrorMessage(importError, 'Could not start watching those campaigns'));
    } finally {
      setBusy(false);
    }
  };

  // Import the whole account WITHOUT watching — same split as Meta: import is
  // unlimited, monitoring stays a deliberate, capped choice.
  const handleImportAll = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const result = await importCampaigns({ userId, connectionId: connection.connection_id, all: true });
      await refreshCampaigns(connection);
      setMessage(`Imported ${result.synced} campaign${result.synced === 1 ? '' : 's'}. Watching stays limited to ${CAMPAIGN_LIMIT} — choose which below.`);
    } catch (importError) {
      setError(campaignErrorMessage(importError, 'Could not import campaigns'));
    } finally {
      setBusy(false);
    }
  };

  const handleUnwatch = async (platformCampaignId) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await stopWatchingCampaign({ userId, connectionId: connection.connection_id, platformCampaignId });
      await refreshCampaigns(connection);
    } catch (unwatchError) {
      setError(campaignErrorMessage(unwatchError, 'Could not stop watching that campaign'));
    } finally {
      setBusy(false);
    }
  };

  if (user.preview) return null; // no demo dataset for Google Ads yet
  if (loading) return <SourcesLoading platform="Google Ads" />;

  const connected = tokenIsUsable(connection?.token);
  const active = connected && Boolean(connection?.ad_account_id);
  const owner = !connection?.access_role || connection.access_role === 'owner';
  const awaitingBilling = active && connection?.setup_state === 'awaiting_billing';

  return (
    <>
      {busy && <SourcesProgress platform="Google Ads" updating />}
      {active && (
        <div className="connection-summary" aria-label="Google Ads connection status">
          <span className="summary-pill">Google Ads connected</span>
          <span className="summary-pill">{connection.ad_account_id}</span>
          {watchedList.length > 0 && <span className="summary-pill">{watchedList.length} campaign{watchedList.length === 1 ? '' : 's'} watched</span>}
          {unwatchedImported.length > 0 && <span className="summary-pill">{imported.length} imported</span>}
          {owner && <button type="button" className="summary-action" disabled={busy} onClick={handleDisconnect}>Disconnect</button>}
        </div>
      )}
      {awaitingBilling && (
        <SetupChecklist
          customerId={connection.ad_account_id}
          businessName={provisionedName || 'your business'}
          invitationEmail={connection.setup_email}
          user={user}
          busy={busy}
          onAcknowledged={handleAcknowledge}
        />
      )}
      {error && <div className="notice notice-error" role="alert">{error}</div>}
      {message && <div className="notice" role="status">{message}</div>}
      <section className="card" aria-label="Google Ads connection">
        {active ? (
        <>
          <div className="section-heading" style={{ marginTop: 0 }}>
            <div>
              <h2>{watchedList.length ? 'Campaigns AdPilot watches' : 'Choose your campaigns'}</h2>
              <span>{watchedList.length ? `${watchedList.length} of ${CAMPAIGN_LIMIT} monitoring slots used.` : `Watch up to ${CAMPAIGN_LIMIT}. Choose the campaigns AdPilot should analyze.`}</span>
            </div>
            {watchedList.length > 0 && (
              <button type="button" className="btn btn-secondary" disabled={busy} onClick={() => setShowImporter((value) => !value)}>
                {showImporter ? 'Done' : 'Manage monitoring'}
              </button>
            )}
          </div>

          {(watchedList.length === 0 || showImporter) && (
            <>
              {available.length === 0 && !error && <p className="card-copy">No campaigns are visible on this Google Ads account yet.</p>}
              {available.length > 0 && (
                <div className="campaign-actions">
                  <button type="button" className="btn btn-primary" disabled={busy || selectedIds.size === 0} onClick={() => handleWatchSelected()}>
                    {busy ? 'Updating monitoring…' : `Watch selected (${selectedIds.size})`}
                  </button>
                  {owner && unimportedCount > 0 && <button type="button" className="btn btn-secondary" disabled={busy} onClick={handleImportAll}>Import all {available.length} without watching</button>}
                </div>
              )}
              <div className="campaign-grid" aria-label="Available Google Ads campaigns">
                {available.map((campaign) => {
                  const watched = watchedIds.has(String(campaign.platform_campaign_id));
                  return (
                    <label key={campaign.platform_campaign_id} className="campaign-option">
                      <input
                        type="checkbox"
                        aria-label={`${watched ? 'Stop watching' : 'Select'} ${campaign.name || `Campaign ${campaign.platform_campaign_id}`}`}
                        checked={watched || selectedIds.has(campaign.platform_campaign_id)}
                        disabled={busy || (!watched && !selectedIds.has(campaign.platform_campaign_id) && selectedIds.size >= slots)}
                        onChange={() => (watched ? handleUnwatch(campaign.platform_campaign_id) : toggleCampaign(campaign.platform_campaign_id))}
                      />
                      <span className="campaign-option-copy">
                        <strong title={campaign.name}>{campaign.name || `Campaign ${campaign.platform_campaign_id}`}</strong>
                        <small>{[campaign.objective, campaign.status].filter(Boolean).join(' · ') || 'Google Ads campaign'}</small>
                      </span>
                    </label>
                  );
                })}
              </div>
            </>
          )}

          {watchedList.length > 0 && !showImporter && (
            <div className="synced-list" aria-label="Watched Google Ads campaigns">
              {watchedList.map((campaign) => (
                <div key={campaign.platform_campaign_id} className="synced-row">
                  <strong>{campaign.name || `Campaign ${campaign.platform_campaign_id}`}</strong>
                  <span>{campaign.status || campaign.objective || 'Synced'}</span>
                  <span>{campaign.last_sync_at || campaign.imported_at ? `Updated ${new Date(campaign.last_sync_at || campaign.imported_at).toLocaleDateString()}` : 'Synced'}</span>
                  <WatchedCampaignActions campaign={campaign} connection={connection} user={user} disabled={busy}
                      onChanged={(updated) => setImported((current) => current.map((item) => String(item.platform_campaign_id) === String(updated.platform_campaign_id) ? updated : item))}
                      onUnwatch={() => handleUnwatch(campaign.platform_campaign_id)} />
                </div>
              ))}
            </div>
          )}

          {unwatchedImported.length > 0 && !showImporter && (
            <>
              <div className="section-heading">
                <div><h2>Imported, not watched</h2><span>Synced from Google Ads and ready to watch when a slot is free. AdPilot does not analyze these.</span></div>
              </div>
              <div className="synced-list">
                {unwatchedImported.map((campaign) => (
                  <div key={campaign.platform_campaign_id} className="synced-row">
                    <strong>{campaign.name || `Campaign ${campaign.platform_campaign_id}`}</strong>
                    <span>{campaign.status || campaign.objective || 'Synced'}</span>
                    <span>{campaign.last_sync_at || campaign.imported_at ? `Updated ${new Date(campaign.last_sync_at || campaign.imported_at).toLocaleDateString()}` : 'Synced'}</span>
                    <button type="button" className="text-button" disabled={busy || slots === 0} onClick={() => handleWatchSelected([campaign.platform_campaign_id])}>{slots === 0 ? 'No slots free' : 'Watch'}</button>
                  </div>
                ))}
              </div>
            </>
          )}

          {owner && unimportedCount > 0 && !showImporter && (
            <div className="section-heading">
              <div><h2>More campaigns available</h2><span>Import {unimportedCount} more campaign{unimportedCount === 1 ? '' : 's'} to keep them ready without adding them to monitoring.</span></div>
              <button type="button" className="btn btn-secondary" disabled={busy} onClick={handleImportAll}>Import {unimportedCount} without watching</button>
            </div>
          )}
        </>
        ) : connected ? (
        <>
          <h2 className="card-title">Which Google Ads account should AdPilot watch?</h2>
          <p className="card-copy">These are the accounts your Google login can access.</p>
          {!owner ? (
            <p className="card-copy">Only the workspace owner can select the account.</p>
          ) : customersFailed ? (
            <p className="card-copy">The account list could not be loaded — the error above has the reason. Reload the page to try again.</p>
          ) : customers.length === 0 ? (
            <p className="card-copy">No Google Ads accounts are visible to this Google login.</p>
          ) : (
            <ul className="account-list">
              {customers.map((customer) => (
                <li key={customer.customer_id}>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    disabled={busy || customer.manager}
                    title={customer.manager ? 'Manager (MCC) accounts hold no campaigns of their own' : undefined}
                    onClick={() => selectCustomer(customer.customer_id)}
                  >
                    {customer.name || `Account ${customer.customer_id}`}
                    {customer.manager ? ' — manager account' : ''}
                    {customer.currency ? ` · ${customer.currency}` : ''}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
        ) : (
        <>
          {path === 'choose' && (
            <>
              <h2 className="card-title">Set up Google Ads</h2>
              <p className="card-copy">Two ways in — pick the one that describes you:</p>
              <div className="campaign-grid" style={{ marginTop: 12 }}>
                <button type="button" className="campaign-option" onClick={() => setPath('existing')}>
                  <span className="campaign-option-copy">
                    <strong>I have Google Ads already</strong>
                    <small>Connect my existing account with Google sign-in — budgets, campaigns and history stay where they are.</small>
                  </span>
                </button>
                <button type="button" className="campaign-option" onClick={() => setPath('new')}>
                  <span className="campaign-option-copy">
                    <strong>I’m new to Google Ads</strong>
                    <small>We create a fresh account for your business under AdPilot’s manager — one short form, done here.</small>
                  </span>
                </button>
              </div>
            </>
          )}

          {path === 'existing' && (
            <>
              <h2 className="card-title">Connect your existing Google Ads account</h2>
              <p className="card-copy">Authorize access through Google’s own consent screen. Only the workspace owner can make this link; you stay in control and can revoke it any time.</p>
              <ol className="card-copy" style={{ margin: '0 0 14px', paddingLeft: 20, lineHeight: 1.7 }}>
                <li><strong>Connect Google Ads</strong> opens Google’s own sign-in and consent screen — sign in with the Google account that manages your ads.</li>
                <li>Google asks you to approve <strong>Google Ads access</strong> for AdPilot. We never see your password, and you don’t need any API keys, tokens, or developer credentials.</li>
                <li>Back here, pick which of your Google Ads accounts AdPilot should watch — direct accounts and every account under any manager you can reach are all listed.</li>
                <li>Choose the campaigns to monitor. Nothing publishes without your approval.</li>
              </ol>
              <aside className="notice" aria-label="Google sign-in help">
                <strong>If Google asks “Verify it’s you”</strong>
                <p>Complete the passkey prompt on your device. If it stays on “Verifying it’s you,” choose <strong>More ways to verify</strong> or <strong>Try another way</strong> on Google’s screen and use another method Google offers. Then approve Google Ads access and return here to select your account.</p>
                <a href="https://support.google.com/accounts/answer/13548313" target="_blank" rel="noopener noreferrer">Google’s passkey sign-in help</a>
              </aside>
              <div className="campaign-actions">
                {authorizationUrl
                  ? <a className="btn btn-primary" href={authorizationUrl}>Connect Google Ads</a>
                  : (
                    <button type="button" className="btn btn-primary" disabled={preparingConnection} onClick={retryAuthorization}>
                      {preparingConnection ? 'Preparing connection…' : 'Try connection again'}
                    </button>
                  )}
                <button type="button" className="btn btn-secondary" onClick={() => setPath('choose')}>Back</button>
              </div>
              <details className="card-copy" style={{ marginTop: 14 }}>
                <summary style={{ cursor: 'pointer' }}>How connecting works</summary>
                <ol style={{ marginTop: 10, paddingLeft: 20, lineHeight: 1.7 }}>
                  <li>Your authorization is stored encrypted, and you can revoke it any time at <a href="https://myaccount.google.com/permissions" target="_blank" rel="noopener noreferrer">myaccount.google.com/permissions</a>.</li>
                  <li>Billing stays exactly where it is: Google keeps charging the payment method already on your account; AdPilot only charges the subscription.</li>
                  <li>If your login reaches the account ONLY through a manager (MCC), we still find it — accounts under managers are expanded automatically.</li>
                </ol>
              </details>
            </>
          )}

          {path === 'new' && (
            <>
              <h2 className="card-title">Create your Google Ads account</h2>
              <p className="card-copy">One form — we do the API work, you keep ownership: the account is yours, billing is yours, AdPilot manages campaigns on top.</p>
              <GoogleAdsProvisionForm user={user} busy={busy} onProvisioned={handleProvisioned} onBack={() => setPath('choose')} />
            </>
          )}
        </>
        )}
      </section>
    </>
  );
}

export default GoogleAdsConnectCard;
